import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';

import type { Ability } from '@core/abilities-types';
import { AccountSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import type { Role } from '@core/roles-types';
import type { SelectOption } from '@core/user-interface-legacy';

import { RoleSelectors } from '../store/role.store';

@Injectable({ providedIn: 'root' })
export class RoleSelectDataService {
  private readonly store = inject(Store);

  public roleOptions$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public abilitiesPerRole$: BehaviorSubject<{
    [key: number]: Ability[];
  }> = new BehaviorSubject({});

  private roles: Role[] = [];

  constructor() {
    const store = this.store;

    combineLatest([
      store.select(AccountSelectors.currentCompany).pipe(filterBoolean),
      store.select(RoleSelectors.entities()).pipe(filterBoolean),
    ]).subscribe(([company, roles]) => {
      this.roles = roles;
      this.roleOptions$.next(this.getRoleOptions(roles, !!company.isAdmin));
      this.abilitiesPerRole$.next(this.pickAbilitiesPerRole(roles));
    });
  }

  public getRole(roleId: number): Role {
    return this.roles.find(role => role.id === roleId)!;
  }

  public getRoles(): Role[] {
    return this.roles;
  }

  private getRoleOptions(roles: Role[], isAdmin: boolean): SelectOption[] {
    return [...roles]
      .sort((roleA, roleB) => Number(roleB.default) - Number(roleA.default))
      .filter(role => (!isAdmin ? !role.admin : true))
      .map(role => ({ value: role.id, label: role.name }));
  }

  private pickAbilitiesPerRole(roles: Role[]): { [key: number]: Ability[] } {
    return roles.reduce(
      (acc, role) => ({
        ...acc,
        [role.id!]: [...role.abilities],
      }),
      {},
    );
  }
}
